const APP_BUNDLE_PATH_PATTERN = /\/js\/app\.[^/]+\.js$/;

// El despliegue completo suele tardar entre 10 y 15 minutos. Comprobar cada
// 15 minutos permite que el backend y el frontend se estabilicen antes de
// forzar una recarga en una pestaña que el usuario pudiera estar utilizando.
export const DEFAULT_BUNDLE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

// Si el usuario vuelve a la pestaña poco después de haberla abandonado, no
// comprobamos todavía la versión. Esto evita recargar la aplicación por un
// cambio de foco breve mientras consulta otra ventana o pestaña.
export const DEFAULT_FOCUS_GRACE_PERIOD_MS = 10 * 60 * 1000;

function toBundlePath(url, baseUrl) {
  if (!url) return null;

  try {
    const path = new URL(url, baseUrl).pathname;
    return APP_BUNDLE_PATH_PATTERN.test(path) ? path : null;
  } catch (error) {
    return null;
  }
}

export function getLoadedBundlePath(
  documentObject = document,
  baseUrl = window.location.href
) {
  return Array.from(documentObject.scripts || [])
    .map((script) => toBundlePath(script.src, baseUrl))
    .find(Boolean);
}

export function getBundlePathFromHtml(html, baseUrl) {
  const scriptMatch = html.match(
    /<script[^>]+src=["']([^"']*\/?js\/app\.[^"']+\.js)["']/i
  );

  return scriptMatch ? toBundlePath(scriptMatch[1], baseUrl) : null;
}

export async function hasNewBundle({
  entryUrl,
  fetchImpl = window.fetch.bind(window),
  documentObject = document,
}) {
  // El bundle que ya está en memoria no puede cambiar mientras se ejecuta
  // la SPA. Por eso comparamos su hash con el que referencia el index.html
  // publicado en ese momento.
  const currentBundlePath = getLoadedBundlePath(
    documentObject,
    entryUrl
  );

  if (!currentBundlePath) return false;

  // El parámetro temporal y cache: 'no-store' evitan leer una copia antigua
  // del index.html desde la caché HTTP del navegador o de un proxy.
  const checkUrl = new URL(entryUrl, window.location.href);
  checkUrl.searchParams.set(
    '__bundle_version_check',
    String(Date.now())
  );

  const response = await fetchImpl(checkUrl.toString(), {
    cache: 'no-store',
    credentials: 'same-origin',
  });

  if (!response.ok) return false;

  const html = await response.text();
  const publishedBundlePath = getBundlePathFromHtml(
    html,
    checkUrl.toString()
  );

  return Boolean(
    publishedBundlePath && publishedBundlePath !== currentBundlePath
  );
}

export function hasFocusGracePeriodElapsed(
  lastFocusAt,
  now = Date.now(),
  gracePeriodMs = DEFAULT_FOCUS_GRACE_PERIOD_MS
) {
  // Se usa >= para que, al alcanzar exactamente los 10 minutos, la siguiente
  // comprobación sea válida.
  return now - lastFocusAt >= gracePeriodMs;
}

export function startBundleVersionChecker({
  entryUrl,
  onNewBundle,
  intervalMs = DEFAULT_BUNDLE_CHECK_INTERVAL_MS,
  focusGracePeriodMs = DEFAULT_FOCUS_GRACE_PERIOD_MS,
}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  let stopped = false;
  let checking = false;
  let updateHandled = false;
  // Marca el instante del último foco para proteger procesos que el usuario
  // pueda tener abiertos al cambiar temporalmente de pestaña.
  let lastFocusAt = Date.now();

  const check = async ({ respectFocusGrace = true } = {}) => {
    if (
      stopped ||
      checking ||
      updateHandled ||
      document.visibilityState === 'hidden'
    ) {
      return;
    }

    // Las comprobaciones periódicas también respetan la ventana de protección
    // posterior al foco. Así no se puede producir una recarga inmediatamente
    // después de que el usuario vuelva a la pestaña.
    if (
      respectFocusGrace &&
      !hasFocusGracePeriodElapsed(lastFocusAt, Date.now(), focusGracePeriodMs)
    ) {
      return;
    }

    checking = true;

    try {
      if (
        await hasNewBundle({
          entryUrl,
        })
      ) {
        // Evita repetir la recarga si coinciden el temporizador y los eventos
        // de foco o visibilidad.
        updateHandled = true;
        onNewBundle();
      }
    } catch (error) {
      // A temporary network error must not affect the current application.
      // The next interval or visibility change will retry the check.
      console.warn('Unable to check for a new frontend bundle', error);
    } finally {
      checking = false;
    }
  };

  const checkWhenVisible = () => {
    // Actualizamos siempre el foco para que un cambio breve de pestaña no
    // provoque una recarga al volver inmediatamente. Si han pasado al menos
    // 10 minutos, check() permitirá comparar el bundle publicado.
    lastFocusAt = Date.now();

    if (document.visibilityState === 'visible') {
      check();
    }
  };

  document.addEventListener('visibilitychange', checkWhenVisible);
  window.addEventListener('focus', checkWhenVisible);
  const intervalId = window.setInterval(check, intervalMs);

  // La primera comprobación se hace al arrancar porque el usuario ya está
  // entrando en la aplicación y necesitamos validar el bundle actual.
  check({ respectFocusGrace: false });

  return () => {
    stopped = true;
    window.clearInterval(intervalId);
    document.removeEventListener(
      'visibilitychange',
      checkWhenVisible
    );
    window.removeEventListener('focus', checkWhenVisible);
  };
}
